import { ReactNode, useState, useEffect } from 'react'
import Header from './Header'
import Sidebar from './Sidebar'
import SiteSettingsModal from './SiteSettingsModal'
import DeletedItemsModal from './DeletedItemsModal'
import NotificationsModal from './notifications/NotificationsModal'
import { AppearanceSettingsProvider } from '../contexts/AppearanceSettingsContext'
import { useIsMobile } from '../hooks/useIsMobile'
import { useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { api } from '../lib/api'

interface LayoutProps {
  children: ReactNode
  pageTitle?: string
  hierarchy?: any[]
}

export default function Layout({ children, pageTitle, hierarchy }: LayoutProps) {
  const [showSiteSettings, setShowSiteSettings] = useState(false)
  const [showDeletedItems, setShowDeletedItems] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  
  // Mobile detection and sidebar state
  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(false) // Start closed, update based on mobile detection
  
  // Get current page info if we're on a page route
  const location = useLocation()
  const isPageRoute = location.pathname.startsWith('/page/')
  const pageId = isPageRoute ? location.pathname.split('/page/')[1] : null
  
  // Fetch page data for header if we're on a page route
  const { data: pageData } = useQuery(
    ['page-header', pageId],
    () => api.get(`/pages/${pageId}`).then(res => res.data.data),
    { 
      enabled: !!pageId && isMobile, // Only fetch on mobile to avoid duplicate requests
      staleTime: 5 * 60 * 1000 
    }
  )
  
  // Use passed props or fetched data
  const currentPageTitle = pageTitle || pageData?.title
  const currentHierarchy = hierarchy || pageData?.hierarchy
  
  // Update sidebar state when mobile detection changes
  useEffect(() => {
    setSidebarOpen(!isMobile) // Desktop: open, Mobile: closed
  }, [isMobile])

  return (
    <AppearanceSettingsProvider>
      <div className="h-screen bg-background flex flex-col overflow-hidden">
        <Header 
          onOpenSiteSettings={() => setShowSiteSettings(true)} 
          onOpenDeletedItems={() => setShowDeletedItems(true)}
          onOpenNotifications={() => setShowNotifications(true)}
          isMobile={isMobile}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          pageTitle={currentPageTitle}
          hierarchy={currentHierarchy}
        />
        <div className="flex flex-1 overflow-hidden">
          {(!isMobile || sidebarOpen) && (
            <Sidebar 
              isMobile={isMobile}
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />
          )}
          <main className={`flex-1 ${isMobile ? 'p-2' : 'p-6'} overflow-hidden`}>
            {children}
          </main>
        </div>

        {/* Site Settings Modal - Rendered at layout level for proper z-index */}
        <SiteSettingsModal 
          isOpen={showSiteSettings} 
          onClose={() => setShowSiteSettings(false)} 
        />

        {/* Deleted Items Modal */}
        <DeletedItemsModal 
          isOpen={showDeletedItems} 
          onClose={() => setShowDeletedItems(false)} 
        />

        {/* Notifications Modal */}
        <NotificationsModal 
          isOpen={showNotifications} 
          onClose={() => setShowNotifications(false)} 
        />
      </div>
    </AppearanceSettingsProvider>
  )
}
